import React, { useEffect, useRef, useState } from 'react'

import { Box, Checkbox, Link, Stack, Typography, useTheme } from '@mui/material'
import { Button } from '@repo/ui'
import { CircleCheck } from 'lucide-react'

import { InputOTP, InputOTPGroup, InputOTPSlot } from '@repo/shadcn-ui'

import { DrawerPopup } from 'components/UI/DrawerPopup'
import { LegalDocumentDrawer } from 'components/UI/LegalDocumentDrawer'
import { FieldLabel } from 'components/UI/FieldLabel'
import { CodeExpiryNotice } from 'components/auth'
import { OTP_CODE_TTL_SECONDS, useCodeExpiry } from 'hooks/useCodeExpiry'
import { PhoneField } from 'components/Fields/PhoneField'
import { SMS_CONSENT_TEXT, TERMS_URL, PRIVACY_URL } from 'constants/smsConsent'
import { createApiCall } from 'services'
import { useDispatch } from 'store/hooks'
import { getProfile } from 'store/auth/actions'

type Step = 'phone' | 'code' | 'success'
type LegalDoc = 'terms' | 'privacy' | null

type Props = {
  open: boolean
  onClose: () => void
  /** Seeds the phone step with the customer's existing (unverified) number, if any. */
  initialPhone?: string | null
  /**
   * Which page opened the drawer. Some copy below references controls that
   * only exist on the Notifications page (the SMS on/off switches), so it
   * must read differently when opened from Profile, which has no such
   * controls of its own (VERA-291 follow-up — the drawer is shared between
   * both pages, working-conventions §36).
   */
  surface: 'notifications' | 'profile'
}

const CODE_LENGTH = 4

/**
 * VERA-291 — phone verification, two steps in a single content-height bottom
 * drawer (per working-conventions §, "bottom drawers are content-height" —
 * `fullScreen={false}` below, never the DrawerPopup default of 100vh).
 *
 * Shared by the Notifications page and the Profile page (moved under
 * components/UI on its second consumer, per working-conventions §36) so both
 * entry points get the same consent + OTP verification flow rather than
 * Profile's previous bare, unverified phone save.
 *
 * Calls the phone-verify endpoints directly via `createApiCall` (the existing
 * pattern for self-contained drawer flows — see `CancelTransferDrawer`) rather
 * than adding a redux saga for a two-step, drawer-local flow. On confirmed
 * success it dispatches `getProfile()` so any consumer reading `phone` /
 * `phone_verified` off the shared auth profile (Notifications' SMS switches,
 * Profile's General section) refreshes without a full page reload.
 */
export const PhoneVerificationDrawer: React.FC<Props> = ({ open, onClose, initialPhone, surface }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  /*
   * VRG-720 — send time of the outstanding code.
   *
   * 10 minutes is this code's real TTL and not an assumption: the API passes
   * `validityPeriodSeconds: OTP_TTL_SECONDS` to Twilio Verify in
   * `RequestPhoneVerificationUseCase`, so the same constant governs the text
   * as governs the emailed sign-in code.
   */
  const [codeIssuedAt, setCodeIssuedAt] = useState<number | null>(null)
  const { expired: codeExpired } = useCodeExpiry(codeIssuedAt, OTP_CODE_TTL_SECONDS)
  const [reference, setReference] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // Affirmative SMS consent — never pre-checked, and reset on every open so a
  // previous session's tick can't carry over (A2P requires an active choice).
  const [consented, setConsented] = useState(false)
  // Which legal document is open in the in-app viewer, if any (VERA-291).
  const [legalDoc, setLegalDoc] = useState<LegalDoc>(null)

  // Seeded from the latest profile, but read through a ref so it is NOT an
  // effect dependency. It used to be one, and that was a real bug: confirming
  // the code dispatches getProfile(), which changes `initialPhone`, which
  // re-fired this reset while the drawer was still open — so the success step
  // was wiped and the user was thrown back to the phone step (with the country
  // flag reset) the instant verification succeeded.
  const initialPhoneRef = useRef(initialPhone)
  initialPhoneRef.current = initialPhone

  // Reset ONLY on the closed → open transition, so a stale success/code step
  // from a previous open never reappears.
  useEffect(() => {
    if (open) {
      setStep('phone')
      setPhone(initialPhoneRef.current ?? '')
      setCode('')
      setCodeIssuedAt(null)
      setReference(null)
      setError(null)
      setSubmitting(false)
      setConsented(false)
      setLegalDoc(null)
    }
  }, [open])

  const requestCode = async () => {
    if (!phone.trim() || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const { status, data } = await createApiCall({
        method: 'POST',
        url: '/auth/customer/phone/verify/request',
        data: { phone: phone.trim() },
      })
      if (status === 201 && data?.reference) {
        setReference(data.reference)
        setCode('')
        setCodeIssuedAt(Date.now())
        setStep('code')
      } else if (status === 429) {
        setError("You've requested too many codes. Please wait 15 minutes and try again.")
      } else if (status === 400) {
        // A rejected number comes back through the DTO validation pipe, whose
        // envelope message is the literal string "Validation errors" — true but
        // useless to a person staring at a phone field. Always prefer our own
        // copy here and tell them what to actually DO.
        setError('Enter a valid phone number, including the country code.')
      } else {
        setError(data?.message || "We couldn't send a code to that number. Check it and try again.")
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const confirmCode = async () => {
    if (!reference || code.trim().length !== CODE_LENGTH || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const { status, data } = await createApiCall({
        method: 'POST',
        url: '/auth/customer/phone/verify/confirm',
        data: { reference, code: code.trim() },
      })
      if (status === 201 && data?.phone_verified) {
        setStep('success')
        dispatch(getProfile())
      } else if (status === 404) {
        setError('That code has expired. Request a new one.')
      } else if (status === 400) {
        setError("That code isn't right. Try again.")
      } else {
        setError(data?.message || "We couldn't verify that code. Try again.")
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const title = step === 'phone' ? 'Verify phone number' : step === 'code' ? 'Enter verification code' : 'Phone verified'

  const drawer = (
    <DrawerPopup
      title={title}
      handleOpen={open}
      handleClose={onClose}
      fullScreen={false}
      child={
        step === 'phone' ? (
          <Stack spacing={4}>
            <Stack>
              <FieldLabel label='Phone number' />
              <PhoneField value={phone} onChange={v => { setPhone(v); setError(null) }} error={Boolean(error)} />
            </Stack>

            {/*
              US carriers (A2P 10DLC) require a web opt-in to carry all of this
              before any SMS is sent: an affirmative, NOT pre-selected consent
              checkbox, what the messages are, how often, the rates disclaimer,
              HELP/STOP, and links to Terms and Privacy. A verification prompt
              alone is not consent — this drawer previously said only "we'll
              text a code", which is why it would have failed campaign review.
              The submit stays disabled until the box is ticked, so consent is
              never implied by proceeding.

              ALL FOUR DISCLOSURES LIVE IN THE CHECKBOX LABEL — do not split them
              back out. Our first A2P campaign was REJECTED (2026-08-03) for
              "opt-in flow doesn't contain compliant consent agreement language".
              The page did carry every element, but the *consent agreement* (the
              label you tick) named only the message types; frequency, rates and
              STOP/HELP sat in a sibling paragraph below. A carrier reviewer
              applies the CTIA checklist to the statement being agreed to, so
              three of four elements read as absent. Anything moved out of this
              label is, to a reviewer, not consented to.

              The exact strings matter too. "Message frequency varies" and
              "Consent is not a condition of purchase" are the phrases reviewers
              pattern-match; the earlier bespoke wording ("frequency depends on
              your account activity", "Consent is not required to use Vera") was
              substantively equivalent and still read as non-compliant.

              SMS_CONSENT_TEXT (constants/smsConsent.ts) is the single source of
              truth for this statement — public/sms.html (the public page for
              carrier review) is generated from the same constant, so the two
              can no longer drift apart. See that file's header comment.
            */}
            <Stack direction='row' spacing={2} alignItems='flex-start'>
              <Checkbox
                checked={consented}
                onChange={(_, next) => { setConsented(next); setError(null) }}
                sx={{
                  p: 0,
                  mt: '2px',
                  // Unchecked is no longer set here: `settings/mui/MuiCheckbox.ts`
                  // paints `text.primary`, which is what this site was overriding to
                  // when the base default was `primary.contrastText` (white on the
                  // drawer's white background — invisible, on the one element
                  // carriers require to be visibly unchecked).
                  //
                  // Checked STAYS the brand accent: this is a consent control the
                  // reviewer must see as deliberately ticked, so it takes the
                  // tenant's colour rather than the neutral base.
                  '&.Mui-checked': { color: theme.palette.primary.main },
                }}
                inputProps={{ 'aria-label': 'Consent to receive text messages from Vera' }}
              />
              <Typography variant='body3' sx={{ color: theme.palette.text.primary, lineHeight: 1.5 }}>
                {SMS_CONSENT_TEXT}
              </Typography>
            </Stack>

            {/*
              These two lines are both short, secondary asides (not more
              consent copy) — grouped into their own tight Stack so they read
              as one small block rather than two paragraphs each given the
              same 32px weight as the consent statement above and the Send
              Code button below (VERA-291 follow-up: "vertical rhythm is
              uneven ... reads dense"). Spacing/layout only — the wording is
              untouched.
            */}
            <Stack spacing={1.5}>

              <Typography variant='body3' sx={{ color: theme.palette.text.secondary }}>
                {/*
                  These are secondary reference links, not calls to action —
                  the app-wide MuiLink theme override (fontWeight: 600,
                  fontSize: 14px) makes every Link bold by default, which is
                  right for an in-content CTA link but too loud here. Override
                  locally rather than touching the shared MuiLink override,
                  which other links in the app may rely on.
                */}
                <Link
                  component='button'
                  type='button'
                  onClick={() => setLegalDoc('terms')}
                  sx={{ fontWeight: 400, fontSize: '12px', verticalAlign: 'baseline' }}
                  color='inherit'
                >
                  Terms and Conditions
                </Link>
                {' · '}
                <Link
                  component='button'
                  type='button'
                  onClick={() => setLegalDoc('privacy')}
                  sx={{ fontWeight: 400, fontSize: '12px', verticalAlign: 'baseline' }}
                  color='inherit'
                >
                  Privacy Policy
                </Link>
              </Typography>
            </Stack>

            {error && (
              <Typography variant='body3' sx={{ color: theme.palette.error.main }}>
                {error}
              </Typography>
            )}
            <Button
              variant='primary'
              fullWidth
              loading={submitting}
              disabled={submitting || !phone.trim() || !consented}
              onClick={requestCode}
            >
              Send code
            </Button>
          </Stack>
        ) : step === 'code' ? (
          <Stack spacing={4}>
            <Typography variant='body2' sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              Enter the {CODE_LENGTH}-digit code we sent to {phone}.
            </Typography>
            <Stack>
              {/*
                VERA-291 — one-to-one parity with the app's real sign-in
                verification-code screen (`SignInBanner.tsx`, and
                `ForgotPassword.tsx`, which both go through the same
                magic-auth code step): the SAME `InputOTP` digit component
                from `@repo/shadcn-ui`, not the app's own `OtpInput`
                (components/UI/OtpInput.tsx — extracted for this drawer
                before the login screen's real component was checked
                against, and still used by EditEmailDrawer). Reusing the
                real component means the code box here looks and behaves
                exactly like the one users already know from signing in,
                just resized to `CODE_LENGTH` slots (4, the SMS code length)
                instead of the login flow's 6.
              */}
              <FieldLabel label='Verification code' />
              <InputOTP
                value={code}
                onChange={(value: string) => {
                  const cleaned = value.replace(/\D/g, '').slice(0, CODE_LENGTH)
                  setCode(cleaned)
                  setError(null)
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (!submitting) confirmCode()
                  }
                }}
                maxLength={CODE_LENGTH}
                disabled={submitting}
                id='phone-verify-otp'
                containerClassName='w-full'
              >
                <InputOTPGroup className='flex w-full gap-2 *:data-[slot=input-otp-slot]:rounded-md *:data-[slot=input-otp-slot]:border'>
                  {Array.from({ length: CODE_LENGTH }).map((_, index) => (
                    <InputOTPSlot key={index} className='rounded-md border flex-1 h-12' index={index} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {error && (
                <Typography
                  variant='body3'
                  sx={{ color: theme.palette.error.main, mt: 2 }}
                >
                  {error}
                </Typography>
              )}
            </Stack>
            {/*
              VERA-291: Verify + Resend grouped into their OWN tighter Stack
              (12px, working-conventions §29's apps/app conversion — base
              8px, spacing={1.5}) instead of sitting as two more items in the
              outer spacing={4} Stack (32px in this app's un-overridden MUI
              theme), which read as too far apart for a button pair. Resend
              is `secondary` (bordered), matching the login screen's
              contained-Verify / outlined-Resend pairing rather than a bare
              text link.
            */}
            <CodeExpiryNotice
              issuedAt={codeIssuedAt}
              ttlSeconds={OTP_CODE_TTL_SECONDS}
              resendLabel='Use Resend code below'
            />
            <Stack spacing={1.5}>
              {/*
                VRG-720: Resend takes the fill once the code has expired, and
                Verify steps back — but stays clickable. The countdown predicts
                the server's answer from this device's clock rather than being
                it. Fuller note in OnboardingAuthPanel.
              */}
              <Button
                variant={codeExpired ? 'secondary' : 'primary'}
                fullWidth
                loading={submitting}
                disabled={submitting || code.trim().length !== CODE_LENGTH}
                onClick={confirmCode}
              >
                Verify
              </Button>
              <Button variant={codeExpired ? 'primary' : 'secondary'} fullWidth disabled={submitting} onClick={requestCode}>
                Resend code
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={4} alignItems='center' sx={{ textAlign: 'center', py: 2 }}>
            {/*
              VERA-291: was `theme.palette.brand[600]` — a HARDCODED Vera
              purple (settings/theme.ts) that is the same for every tenant,
              unlike `theme.palette.primary`, which resolves to the
              company's own Studio primary colour (falling back to black for
              Vera's own default). A brand-purple check on every white-label
              tenant is the same bug class as the Terms/Privacy links
              pointing at vera.inc: Vera's own identity leaking into a
              brand's customer-facing surface.
            */}
            <Box sx={{ color: theme.palette.primary.main }}>
              <CircleCheck size={40} />
            </Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 500, color: theme.palette.text.primary }}>
              Phone verified
            </Typography>
            <Typography variant='body2' sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              {surface === 'notifications'
                ? 'You can now turn on SMS notifications below.'
                : 'You can now turn on SMS notifications from your Notifications settings.'}
            </Typography>
            <Button variant='primary' fullWidth onClick={onClose}>
              Done
            </Button>
          </Stack>
        )
      }
    />
  )

  return (
    <>
      {drawer}
      <LegalDocumentDrawer
        open={legalDoc === 'terms'}
        onClose={() => setLegalDoc(null)}
        title='Terms and Conditions'
        url={TERMS_URL}
      />
      <LegalDocumentDrawer
        open={legalDoc === 'privacy'}
        onClose={() => setLegalDoc(null)}
        title='Privacy Policy'
        url={PRIVACY_URL}
      />
    </>
  )
}
